import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalanceWallet as WalletIcon,
  Savings as SavingsIcon,
  ShowChart as ChartIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import apiService from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Styled Components
const KPICard = styled(Card)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
  border: `2px solid ${color}30`,
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${color}40`,
    borderColor: color,
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: color,
  },
}));

const ValueTypography = styled(Typography)(({ theme }) => ({
  fontSize: '2.2rem',
  fontWeight: 700,
  color: '#1a365d',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const TrendChip = styled(Chip)(({ theme, trend }) => ({
  marginTop: theme.spacing(1),
  fontSize: '0.75rem',
  height: 24,
  backgroundColor: trend === 'up' ? 'rgba(56, 161, 105, 0.15)' : 'rgba(229, 62, 62, 0.15)',
  color: trend === 'up' ? '#2f855a' : '#c53030',
  border: `1px solid ${trend === 'up' ? 'rgba(56, 161, 105, 0.3)' : 'rgba(229, 62, 62, 0.3)'}`,
  fontWeight: 600,
  
  '& .MuiChip-icon': {
    fontSize: '1rem',
  },
}));

/**
 * FinanzKennzahlenDashboard
 * 
 * Zeigt wichtige Finanzkennzahlen auf dem Dashboard:
 * - Liquidität (Gesamt-Cashflow aktuell + YTD)
 * - Privat-Konto (Gesamt + YTD)
 * - Darlehen-Saldo
 * - Offene Forderungen
 * - Monatliche Mieteinnahmen
 */
const FinanzKennzahlenDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kennzahlen, setKennzahlen] = useState({
    liquiditaet_gesamt: 0,
    liquiditaet_ytd_veraenderung: 0,
    privat_konto_gesamt: 0,
    privat_konto_ytd: 0,
    darlehen_saldo: 0,
    offene_forderungen_einnahmen: 0,
    offene_forderungen_ausgaben: 0,
    unverarbeitete_kontobewegungen: 0,
    cashflow_trend: 'neutral',
    privat_trend: 'neutral'
  });
  const [liquiditaetVerlauf, setLiquiditaetVerlauf] = useState([]);

  useEffect(() => {
    fetchKennzahlen();
    fetchLiquiditaetVerlauf();
  }, []);

  const fetchKennzahlen = async () => {
    setLoading(true);
    try {
      const response = await apiService.get('/api/dashboard/finanzkennzahlen');
      // apiService gibt bereits response.data zurück
      setKennzahlen(response);
    } catch (err) {
      console.error('Fehler beim Laden der Finanzkennzahlen:', err);
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiquiditaetVerlauf = async () => {
    try {
      const response = await apiService.get('/api/dashboard/liquiditaet-verlauf');
      // apiService gibt bereits response.data zurück
      setLiquiditaetVerlauf(response.verlauf || []);
    } catch (err) {
      console.error('Fehler beim Laden des Liquiditätsverlaufs:', err);
      // Nicht kritisch - Diagramm bleibt leer
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTrend = (value) => {
    if (value > 0) return { trend: 'up', text: `+${formatCurrency(value)}`, icon: TrendingUpIcon };
    if (value < 0) return { trend: 'down', text: formatCurrency(value), icon: TrendingDownIcon };
    return { trend: 'neutral', text: '±0 €', icon: null };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  const liquiditaetTrend = formatTrend(kennzahlen?.liquiditaet_ytd_veraenderung || 0);
  const privatTrend = formatTrend(kennzahlen?.privat_konto_ytd || 0);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 700, color: '#1a365d' }}>
        📊 Finanzkennzahlen
      </Typography>
      
      <Grid container spacing={3}>
        {/* Liquidität (Gesamt-Cashflow) */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard color={theme.palette.primary.main} onClick={() => {}}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Liquidität
                </Typography>
                <WalletIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
              </Box>
              
              <ValueTypography>
                {formatCurrency(kennzahlen?.liquiditaet_gesamt)}
              </ValueTypography>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                Alle Liquiditätskonten
              </Typography>
              
              {liquiditaetTrend.icon && (
                <TrendChip
                  icon={<liquiditaetTrend.icon />}
                  label={`YTD: ${liquiditaetTrend.text}`}
                  trend={liquiditaetTrend.trend}
                  size="small"
                />
              )}
            </CardContent>
          </KPICard>
        </Grid>

        {/* Privat-Konto */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard color={theme.palette.success.main} onClick={() => {}}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Privat-Konto
                </Typography>
                <SavingsIcon sx={{ color: theme.palette.success.main, fontSize: 32 }} />
              </Box>
              
              <ValueTypography>
                {formatCurrency(kennzahlen?.privat_konto_gesamt)}
              </ValueTypography>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                Entnahmen & Einlagen
              </Typography>
              
              {privatTrend.icon && (
                <TrendChip
                  icon={<privatTrend.icon />}
                  label={`YTD: ${privatTrend.text}`}
                  trend={privatTrend.trend}
                  size="small"
                />
              )}
            </CardContent>
          </KPICard>
        </Grid>

        {/* Unverarbeitete Kontobewegungen */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard color={theme.palette.warning.main} onClick={() => window.location.href = '/dashboard/dreistapelverarbeitung'}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Unverarbeitete Bewegungen
                </Typography>
                <TimelineIcon sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
              </Box>
              
              <ValueTypography>
                {kennzahlen?.unverarbeitete_kontobewegungen || 0}
              </ValueTypography>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                Warten auf Verarbeitung
              </Typography>
            </CardContent>
          </KPICard>
        </Grid>

        {/* Offene Forderungen */}
        <Grid item xs={12} sm={6} md={3}>
          <KPICard color="#e53e3e" onClick={() => window.location.href = '/dashboard/forderungsmanagement'}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                  Offene Forderungen
                </Typography>
                <ChartIcon sx={{ color: '#e53e3e', fontSize: 32 }} />
              </Box>
              
              <ValueTypography>
                {formatCurrency(kennzahlen?.offene_forderungen_einnahmen || 0)}
              </ValueTypography>
              
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                Einnahmen (eingehend)
              </Typography>

              {kennzahlen?.offene_forderungen_ausgaben > 0 && (
                <Typography variant="body2" sx={{ color: '#d69e2e', fontWeight: 500 }}>
                  Ausgaben: {formatCurrency(kennzahlen.offene_forderungen_ausgaben)}
                </Typography>
              )}
            </CardContent>
          </KPICard>
        </Grid>
      </Grid>

      {/* Liquiditätsverlauf Diagramm */}
      {liquiditaetVerlauf.length > 0 && (
        <Paper 
          elevation={2} 
          sx={{ 
            mt: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <TimelineIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a365d' }}>
              Liquiditätsverlauf {new Date().getFullYear()} (YTD)
            </Typography>
          </Box>
          
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={liquiditaetVerlauf}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="monat" 
                stroke="#718096"
                style={{ fontSize: '12px' }}
                interval={0}
                angle={0}
                textAnchor="middle"
              />
              <YAxis 
                stroke="#718096"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => {
                  const absValue = Math.abs(value);
                  if (absValue >= 1000) {
                    return `${(value / 1000).toFixed(1)}k €`;
                  }
                  return `${value.toFixed(0)} €`;
                }}
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Legend verticalAlign="bottom" height={32} />
              <Line 
                type="monotone" 
                dataKey="einnahmen" 
                name="Einnahmen (kumuliert)"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line 
                type="monotone" 
                dataKey="ausgaben" 
                name="Ausgaben (kumuliert)"
                stroke={theme.palette.error.main}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
              <Line 
                type="monotone" 
                dataKey="differenz" 
                name="Differenz"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', px: 2 }}>
            <Typography variant="caption" color="textSecondary">
              Gesamt-Liquidität über kompletten Erfassungszeitraum: <strong>{formatCurrency(kennzahlen?.liquiditaet_gesamt)}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary">
              YTD-Veränderung: <strong style={{ color: (kennzahlen?.liquiditaet_ytd_veraenderung || 0) >= 0 ? '#38a169' : '#e53e3e' }}>
                {formatCurrency(kennzahlen?.liquiditaet_ytd_veraenderung)}
              </strong>
            </Typography>
          </Box>
        </Paper>
      )}

      <Divider sx={{ my: 4 }} />
    </Box>
  );
};

export default FinanzKennzahlenDashboard;
